import { Component, Inject, OnInit, signal } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from "@angular/material/dialog";
import { StoreService } from "src/app/services/infra/store/store.service";
import { LoggerService } from "src/app/services/infra/logger/logger.service";
import { BehaviorSubject, timer } from "rxjs";
import { SortColumn } from "src/app/models/sortColumn.model";
import { ITableColumn } from "src/app/models/tableColumns";
import { ragCleanupTableColumns } from "src/app/models/tableColumns/ragCleanupColumns.model";
import { RagCleanupTableRowViewModel } from "src/app/models/viewModels/RagCleanupTableRowViewModel";
import { MenuAction } from "src/app/models/menuAction.model";
import * as uuid from "uuid";
import { RagCleanupSummaryDialogComponent } from "../rag-cleanup-summary-dialog/rag-cleanup-summary-dialog.component";
import { RagFile } from "src/app/models/api/issue.model";

export interface RagCleanupDialogData {
    files: RagFile[];
}

@Component({
    selector: "app-rag-cleanup-dialog",
    templateUrl: "./rag-cleanup-dialog.component.html",
    styleUrls: ["./rag-cleanup-dialog.component.scss"],
})
export class RagCleanupDialogComponent implements OnInit {
    public waitingForResponse = false;
    public sortedColumn$ = new BehaviorSubject<SortColumn | undefined>(undefined);
    public pageInfo$ = new BehaviorSubject({
        number: 1,
        currSize: 1,
        maxSize: 2,
        totalItems: 1,
        totalPages: 1,
    });
    public isLoading$ = new BehaviorSubject<boolean>(false);
    public columns = signal<ITableColumn[]>(ragCleanupTableColumns);
    public filesCurrentPage$ = new BehaviorSubject<RagCleanupTableRowViewModel[]>([]);
    public showNoData = false;
    public totalItems = 0;
    public multiSelectedMenuActions$: BehaviorSubject<MenuAction[]> = new BehaviorSubject<MenuAction[]>([]);
    public checkedRows = new Set<string>();

    constructor(
        public dialogRef: MatDialogRef<RagCleanupDialogComponent>,
        private _matDialog: MatDialog,
        private _logger: LoggerService,
        @Inject(MAT_DIALOG_DATA) public data: RagCleanupDialogData,
        private _store: StoreService,
    ) {}

    ngOnInit(): void {
        this.totalItems = this.data.files.length;
        this.showNoData = this.data.files.length === 0;
        this.filesCurrentPage$.next(this.data.files.map((item) => this._transformToViewModel(item)));
        this.pageInfo$.next({
            number: 1,
            currSize: this.data.files.length,
            maxSize: 50,
            totalItems: this.data.files.length,
            totalPages: 1,
        });
    }

    public onClose(): void {
        this.dialogRef.close();
    }

    public rowUnselected(): void {
        console.log("Row unselected");
    }

    public onCheckboxClick(event: Set<string>): void {
        this.checkedRows = event;
    }

    public onExcludeClick(): void {
        this.waitingForResponse = true;
        timer(1000).subscribe(() => {
            const dialogContainer = document.querySelector(".rag-cleanup-dialog");
            const rect = dialogContainer!.getBoundingClientRect();
            const width = `${rect.width}px`;
            const height = `${rect.height}px`;

            this._matDialog.open(RagCleanupSummaryDialogComponent, {
                data: { excludedFiles: this.filesCurrentPage$.value.filter((item) => this.checkedRows.has(item.rowId)) },
                disableClose: false,
                panelClass: "sized-popup-dialog-container",
                width: `${width}`,
                height: `${height}`,
            });
            this.dialogRef.close();
        });
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-explicit-any
    public rowSelected(selectedRow: any): void {}

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public multiSelectedActionClick(menuAction: MenuAction): void {}

    private _transformToViewModel(row: RagFile): RagCleanupTableRowViewModel {
        return {
            fileName: row.fileName,
            fileSize: row.fileSize,
            uploadDate: row.uploadDate,
            scanResults: row.scanResults,
            fileIcon: `assets/images/logo/agent-client-app-${row.fileIcon}.svg`,
            rowId: uuid.v4(),
            clickable: true,
            scanResultTextColor: row.scanResults === "Malicious" ? "red" : row.scanResults === "Safe" ? "green" : undefined,
            scanResultIcon:
                row.scanResults === "Malicious"
                    ? "assets/images/status-icons/error.svg"
                    : row.scanResults === "Safe"
                      ? "assets/images/status-icons/healthy.svg"
                      : undefined,
        };
    }
}
